import React, { Fragment } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { Link } from 'react-router-dom'

import { formatDate, formatNumber } from '@atheneum/api/dist/lib'

import Button from '@components/Button'
import Spinner from '@components/spinner'
import RenderField from '../Components/RenderField'

function ConsultationDetailsSidebar ({
  expertPaymentsStore,
  sidebarStore,
  modalStore,
  growlStore
}) {
  const { sidebarData, staticData, search } = expertPaymentsStore
  const { epdData, cpdData, feeData, loading } = sidebarData

  if (loading) return <Spinner />

  const feeItem = get(feeData, 'feeItems', []).find(fi => fi.feeTypeId)
  const costItem = get(feeData, 'feeItems', []).find(fi => fi.costTypeId)

  const feeType = get(feeItem, 'feeTypeId')
  const honorarium =
    costItem &&
    `${formatNumber(costItem.value)} ${
      staticData.names.currencies[costItem.currencyId]
    }`

  const consultationDuration =
    feeData.consultationDuration && `${feeData.consultationDuration} min`

  return (
    <div>
      <h1
        className='sidebar-wrapper__content__link'
        onClick={() => {
          sidebarStore.closeSidebar()
          modalStore.show({
            type: 'CPD_FORM',
            props: {
              cpd: cpdData,
              epd: epdData,
              onSubmitSuccess: () => {
                growlStore.showSuccess('CPD updated successfully')
                modalStore.close()
                search()
              },
              onSubmitError: error => {
                growlStore.showError(error)
              }
            }
          })
        }}
      >
        {cpdData.expert.fullName}
        <br />
        Consultation details
      </h1>
      <RenderField
        label='Payment status'
        dataType='paymentStatuses'
        value={cpdData.paymentStatusId}
      />
      <RenderField label='Project name' value={feeData.project.projectName} />
      <RenderField label='Fee type' value={feeType} dataType='feeTypes' />
      <RenderField label='Honorarium' value={honorarium} />
      <RenderField label='Consultation duration' value={consultationDuration} />
      <h2>
        <Link to={`/expert/${epdData.expertId}/payment-details`}>
          Expert payment details
        </Link>
      </h2>
      <ExpertPaymentDetails
        epd={epdData}
        cpd={cpdData}
        expertPaymentsStore={this.props.expertPaymentsStore}
      />
    </div>
  )
}

ConsultationDetailsSidebar.propTypes = {
  sidebarStore: PropTypes.object.isRequired,
  modalStore: PropTypes.object.isRequired,
  growlStore: PropTypes.object.isRequired,
  expertPaymentsStore: PropTypes.object.isRequired
}

export default inject('sidebarStore', 'modalStore', 'growlStore')(
  observer(ConsultationDetailsSidebar)
)

function ExpertPaymentDetails ({ expertPaymentsStore }) {
  const { sidebarData, staticData } = expertPaymentsStore
  const { epdData, cpdData } = sidebarData
  const {
    DIRECT_TRANSFER,
    DONATION_OTHER,
    PAYPAL,
    DONATION_CHILDREN_FOR_A_BETTER_WORLD
  } = staticData.CONST.PAYMENT_TRANSFER_METHODS
  const { COLLECTING_PAYMENT_DETAILS, PAID } = staticData.CONST.PAYMENT_STATUSES

  const { countries } = staticData.names
  const { expertsAddress, bankAddress } = epdData

  const rejectionReason =
    epdData.epdRejectionReasonId && epdData.epdRejectionReason.name
  const rejectionDate =
    epdData.epdRejectionReasonId && formatDate(epdData.rejectionDate)

  const paypalEmail =
    epdData.paymentTransferMethodId === PAYPAL && epdData.payPalEmail

  const transferMethodDonation =
    epdData.paymentTransferMethodId === DONATION_CHILDREN_FOR_A_BETTER_WORLD &&
    epdData.paymentTransferMethodId

  const comment = transferMethodDonation && epdData.comment

  const isBankTransfer = [DIRECT_TRANSFER, DONATION_OTHER].includes(
    epdData.paymentTransferMethodId
  )

  const expertAddressFields = isBankTransfer && epdData.addressId && (
    <div>
      {expertsAddress.address1 && <div>Address: {expertsAddress.address1}</div>}
      {expertsAddress.address2 && (
        <div>Address 2: {expertsAddress.address2}</div>
      )}
      {expertsAddress.city && <div>City: {expertsAddress.city}</div>}
      {expertsAddress.state && <div>State: {expertsAddress.state}</div>}
      {expertsAddress.countryId && (
        <div>Country: {countries[expertsAddress.countryId]}</div>
      )}
    </div>
  )

  const bankingDetailsFields = isBankTransfer && epdData.bankAddressId && (
    <div>
      {epdData.bankName && <div>Bank name: {epdData.bankName}</div>}
      {epdData.bankCode && <div>ZIP: {epdData.bankCode}</div>}
      {bankAddress.city && <div>City: {bankAddress.city}</div>}
      {bankAddress.state && <div>State: {bankAddress.state}</div>}
      {bankAddress.countryId && (
        <div>Country: {countries[bankAddress.countryId]}</div>
      )}
    </div>
  )

  return (
    <Fragment>
      <RenderField
        label='Status'
        dataType='expertPaymentDetailStatuses'
        value={epdData.expertPaymentDetailStatusId}
      />
      {rejectionReason && (
        <RenderField label='Rejected' value={rejectionReason} />
      )}
      {rejectionDate && (
        <RenderField label='Rejection date' value={rejectionDate} />
      )}
      {expertAddressFields && (
        <RenderField label='Address' value={expertAddressFields} />
      )}
      {bankingDetailsFields && (
        <RenderField label='Banking details' value={bankingDetailsFields} />
      )}
      {paypalEmail && <RenderField label='Paypal email' value={paypalEmail} />}
      {transferMethodDonation && (
        <RenderField
          label='Payment type'
          value={transferMethodDonation}
          dataType='paymentTransferMethods'
        />
      )}
      {comment && <RenderField label='Comment' value={comment} />}
      {cpdData.paymentStatusId === COLLECTING_PAYMENT_DETAILS && (
        <Fragment>
          <div className='group'>
            <Button
              primary
              onClick={() =>
                expertPaymentsStore.requestEpdConfirmation(epdData)
              }
            >
              Request EPD confirmation
            </Button>
          </div>
          <div className='group'>
            <small>
              Last request was sent on:{' '}
              {formatDate(epdData.confirmationRequestSentDate)}
            </small>
          </div>
        </Fragment>
      )}
      {cpdData.paymentStatusId === PAID && (
        <div className='group'>
          <Button
            primary
            onClick={() => expertPaymentsStore.exportInvoice(cpdData.id)}
          >
            Export invoice
          </Button>
        </div>
      )}
    </Fragment>
  )
}

ExpertPaymentDetails.propTypes = {
  expertPaymentsStore: PropTypes.object.isRequired
}
